import type {
  AdminAccessReasonCategoryModel,
  UserRoleModel,
} from '@/api/generated/models';
import {
  adminAccessControllerEnd,
  adminAccessControllerIssueToken,
} from '@/api/generated/uppleAPIV2';
import { isAxiosError } from 'axios';
import toast from 'react-hot-toast';
import { ADMIN_ACCESS_ENDED_PATH } from './admin-access-navigation';
import { fetchMeWithToken } from './auth-service';
import {
  getAdminAccessState,
  getAuthData,
  markAdminAccessEnded,
  startAdminAccessTab,
} from './auth-storage';

// 서버 ADMIN_ACCESS_REASON_CATEGORY_LABELS 와 같은 문구다. 바꾸면 서버도 함께 바꾼다(수동 동기화).
export const ADMIN_ACCESS_REASON_LABELS: Record<
  AdminAccessReasonCategoryModel,
  string
> = {
  CS_REPRODUCTION: 'CS 문의 재현',
  DATA_FIX: '데이터 수정 요청 처리',
  VERIFICATION: '기능 확인·검증',
  OTHER: '기타',
};

export const USER_ROLE_LABELS: Record<UserRoleModel, string> = {
  CASE: '대상자',
  COACH: '코치',
  COACHING_MANAGER: 'CPM',
  COACHING_DIRECTOR: '코칭 디렉터',
  HR: 'HR',
};

/** 새 탭이 대리 접속을 열지 못한 이유. 화면에 그대로 보여 준다. */
export class AdminAccessOpenError extends Error {}

/**
 * 새 탭에서 세션 ID 로 대리 접속 토큰을 받아 이 탭을 대상 계정으로 바꾼다.
 *
 * 토큰 요청은 아직 이 탭에 남아 있는 CPM 로그인으로 보낸다. 서버는 세션을 만든 본인에게만,
 * 한 번만 내주므로 주소(세션 ID)가 새어도 다른 사람은 쓸 수 없다.
 */
export async function exchangeAdminAccessToken(
  sessionId: string,
): Promise<void> {
  const impersonator = getAuthData()?.user;
  const response = await adminAccessControllerIssueToken(sessionId).catch(
    (error) => {
      const isUnavailable =
        isAxiosError(error) &&
        error.response?.data?.errorCode === 'ADMIN_ACCESS_TOKEN_UNAVAILABLE';
      throw new AdminAccessOpenError(
        isUnavailable
          ? '이미 열었거나 시간이 지난 대리 접속입니다. CPM 콘솔에서 다시 시작해 주세요.'
          : '대리 접속을 열지 못했습니다. 원래 탭의 로그인을 확인한 뒤 다시 시작해 주세요.',
      );
    },
  );
  const { accessToken, expiresInSeconds, reasonCategory, reasonDetail } =
    response.data;

  // 토큰은 한 번만 받을 수 있어서, 여기서 실패하면 이 세션은 쓸 수 없다. 열린 채 30분을 두지 않고 닫는다.
  const user = await fetchMeWithToken(accessToken).catch(async () => {
    await adminAccessControllerEnd(sessionId).catch(() => {});
    throw new AdminAccessOpenError(
      '대상 계정 정보를 불러오지 못해 대리 접속을 닫았습니다. 잠시 후 다시 시작해 주세요.',
    );
  });

  // 남은 시간은 서버가 센 값으로 이 PC 시계에 맞춘다. PC 시계가 틀려도 배너와 만료 판정이 어긋나지 않는다.
  const expiresAtMs = Date.now() + expiresInSeconds * 1000;
  startAdminAccessTab(
    {
      user: {
        id: user.id,
        email: user.email,
        name: user.name,
        lang: user.lang,
        roles: user.roles || [],
        companyId: user.companyId,
        isRecordingConsented: user.isRecordingConsented ?? null,
        passwordChange: user.passwordChange,
      },
      tokens: {
        accessToken,
        refreshToken: '',
        expiresIn: expiresInSeconds,
        expiresAt: expiresAtMs,
      },
    },
    {
      sessionId,
      expiresAt: new Date(expiresAtMs).toISOString(),
      targetName: user.name,
      targetEmail: user.email,
      impersonatorName: impersonator?.name ?? '',
      reasonLabel: `${ADMIN_ACCESS_REASON_LABELS[reasonCategory]} · ${reasonDetail}`,
    },
  );
}

// 로그아웃 화면은 렌더마다 logout 을 다시 부른다. 종료 요청은 한 번만 보낸다.
let endingPromise: Promise<void> | null = null;

/**
 * 배너의 종료 버튼과 이 탭의 로그아웃이 부른다.
 * 종료 요청이 실패하면 서버 세션이 그대로 열려 있으므로 종료로 표시하지 않고 다시 누를 수 있게 한다.
 */
export function endAdminAccessTab(): Promise<void> {
  endingPromise ??= (async () => {
    const state = getAdminAccessState();
    if (state?.status === 'ACTIVE') {
      try {
        await adminAccessControllerEnd(state.info.sessionId);
      } catch (error) {
        // 401 은 이미 끝난 세션이다. 응답 처리기가 종료 화면으로 보낸다.
        const isAlreadyEnded =
          isAxiosError(error) && error.response?.status === 401;
        if (isAlreadyEnded) return;
        endingPromise = null;
        toast.error(
          '대리 접속을 종료하지 못했습니다. 잠시 후 다시 눌러 주세요.',
        );
        throw error;
      }
      markAdminAccessEnded('ENDED_BY_USER');
    }
    window.location.replace(ADMIN_ACCESS_ENDED_PATH);
  })();
  return endingPromise;
}
